import {MS_PER_DAY} from 'src/features/personal-plan-home/components/pop-quiz/constants';
import {type GetPauseSubscriptionBannerQuery, SubscriptionStatus} from 'src/generated/graphql';

export type PauseSubscriptionEnrollment = NonNullable<
    GetPauseSubscriptionBannerQuery['currentSubscriptionEnrollment']
>;

export const PAUSE_BANNER_DISMISS_MS = 7 * MS_PER_DAY;
export const PAUSE_BANNER_DISMISS_STORAGE_KEY = 'pause-subscription-banner-dismissed-at';
export const SUBSCRIPTION_MANAGEMENT_LINK = '/user/manage-subscriptions/?reactivate=1';

export type PauseBannerPhase = 'before' | 'after';

interface PauseBannerDismissals {
    before?: number;
    after?: number;
}

export function getPauseBannerPhase(status?: string | null): PauseBannerPhase | null {
    if (status === SubscriptionStatus.IntendedToPause) {
        return 'before';
    }
    if (status === SubscriptionStatus.Paused) {
        return 'after';
    }
    return null;
}

function readPauseBannerDismissals(): PauseBannerDismissals {
    try {
        const raw = window.localStorage.getItem(PAUSE_BANNER_DISMISS_STORAGE_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw) as Partial<PauseBannerDismissals>;
        if (!parsed || typeof parsed !== 'object') {
            return {};
        }
        return {
            ...(Number.isFinite(parsed.before) ? {before: parsed.before} : {}),
            ...(Number.isFinite(parsed.after) ? {after: parsed.after} : {}),
        };
    } catch {
        return {};
    }
}

export function isPauseBannerDismissed(phase: PauseBannerPhase, now = Date.now()): boolean {
    const dismissedAt = readPauseBannerDismissals()[phase];
    if (dismissedAt === undefined) {
        return false;
    }
    return now - dismissedAt < PAUSE_BANNER_DISMISS_MS;
}

export function dismissPauseBanner(phase: PauseBannerPhase, now = Date.now()): void {
    try {
        const dismissal = readPauseBannerDismissals();
        dismissal[phase] = now;
        window.localStorage.setItem(PAUSE_BANNER_DISMISS_STORAGE_KEY, JSON.stringify(dismissal));
    } catch {
        // Storage can throw when it is blocked. The banner still hides for this view.
    }
}

export function formatPauseBannerDate(date: Date, locale: string): string {
    const normalizedLocale = locale.replace('_', '-');
    const datePart = date.toLocaleDateString(normalizedLocale, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
    const timePart = date.toLocaleTimeString(normalizedLocale, {
        hour: 'numeric',
        minute: '2-digit',
    });
    return interpolate(gettext('%(datePart)s at %(timePart)s'), {datePart, timePart}, true);
}

export function getPauseBannerContent(phase: PauseBannerPhase, pauseDate: string) {
    if (phase === 'before') {
        return {
            title: interpolate(
                gettext('Your subscription will pause on %(pauseDate)s'),
                {pauseDate},
                true,
            ),
            description: gettext(
                'After that, you will no longer have access to Personal Plan content.',
            ),
        };
    }
    return {
        title: interpolate(
            gettext('Your subscription is paused until %(pauseDate)s'),
            {pauseDate},
            true,
        ),
        description: gettext('Re-activate your subscription to access Personal Plan content.'),
    };
}

export function getPauseBannerState(
    enrollment: PauseSubscriptionEnrollment | null,
    locale: string,
) {
    const phase = getPauseBannerPhase(enrollment?.status);
    if (!phase || !enrollment) {
        return null;
    }
    const rawDate = phase === 'before' ? enrollment.pauseStartDate : enrollment.pauseEndDate;
    if (!rawDate) {
        return null;
    }
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return {phase, pauseDate: formatPauseBannerDate(date, locale)};
}
